import { useCallback, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { vars } from 'src/core/themes';
import { ButtonProps } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { PrintIcon, ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { SaleClient } from 'src/WebApiController';

const PRINT_FRAME_SELECTOR = 'iframe[data-shipping-label-print]';

// Prints a PDF blob through a hidden same-origin iframe (the browser's native
// PDF printing) so the print job contains only the document itself. Stale print
// frames from earlier clicks are removed up front; the blob URL is released on a
// delay since the loaded document stays usable inside the frame.
const printPdfBlob = (blob: Blob) => {
  document
    .querySelectorAll(PRINT_FRAME_SELECTOR)
    .forEach((frame) => frame.parentElement?.removeChild(frame));

  const url = URL.createObjectURL(blob);
  const printFrame = document.createElement('iframe');
  printFrame.setAttribute('data-shipping-label-print', '');
  printFrame.src = url;
  printFrame.style.position = 'fixed';
  printFrame.style.right = '0';
  printFrame.style.bottom = '0';
  printFrame.style.width = '0';
  printFrame.style.height = '0';
  printFrame.style.border = '0';
  printFrame.onload = () => {
    printFrame.contentWindow?.focus();
    printFrame.contentWindow?.print();
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  document.body.appendChild(printFrame);
};

// POS-9480: generates (or re-fetches) the courier shipping label for a
// paper-ticket StubHub sale and prints it straight away - the browser's print
// dialog opens on the fetched PDF through a hidden iframe, with no intermediate
// preview UI. The label wording reflects whether the marketplace sale already
// has a shipment, and flips to "Print" after a successful generate so it stays
// accurate for the rest of the modal session (the sale DTO is only refetched
// when the modal reopens).
export const LaunchPrintShippingLabel = ({
  saleId,
  hasExistingShipment,
  variant,
  iconOnlyMode,
  disabled,
}: {
  saleId: number;
  hasExistingShipment?: boolean | null;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const [isLoading, setIsLoading] = useState(false);
  const [hasGeneratedLabel, setHasGeneratedLabel] = useState(false);

  const onPrint = useCallback(async () => {
    setIsLoading(true);
    await tryInvokeApi(
      async () => {
        const result = await new SaleClient(
          activeAccountWebClientConfig
        ).downloadShippingLabelPdf(saleId);
        if (result.data) {
          setHasGeneratedLabel(true);
          printPdfBlob(result.data);
        }
      },
      (error) => {
        showErrorDialog('SaleClient.downloadShippingLabelPdf', error, {
          trackErrorData: { saleId },
        });
      },
      () => setIsLoading(false)
    );
  }, [activeAccountWebClientConfig, saleId, showErrorDialog]);

  const onClickHandler = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onPrint();
    },
    [onPrint]
  );

  const iconFill =
    iconOnlyMode || (variant && variant !== 'regular')
      ? vars.color.textBrand
      : vars.color.textInverted;

  return (
    <ButtonWithIcon
      disabled={disabled || isLoading}
      variant={variant}
      iconOnlyMode={iconOnlyMode}
      onClick={onClickHandler}
      textContentId={
        hasExistingShipment || hasGeneratedLabel
          ? ContentId.PrintShippingLabel
          : ContentId.GenerateShippingLabel
      }
      icon={
        isLoading ? (
          <RotatingWrapper>
            <ProcessingIcon fill={iconFill} />
          </RotatingWrapper>
        ) : (
          <PrintIcon
            withHoverEffect
            disabled={disabled || isLoading}
            fill={iconFill}
            size={vars.iconSize.m}
          />
        )
      }
    />
  );
};
